import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { toastLoading, toastWarning } from "@/utils/toast";
import { getChildren, getStaffDetail } from "@/api/staff";
import type { StaffChildrenItem } from "@/api/staff";

const BRAND = "#FF6B35";

type Crumb = { id: number; name: string };

function roleName(role?: number) {
  switch (role) {
    case 1: return "会员";
    case 2: return "达人";
    case 3: return "代理商";
    case 4: return "服务商";
    default: return "普通用户";
  }
}

export default function WorkspacePersonnelPage() {
  const navigate = useNavigate();

  const [me, setMe] = useState<{ nickname: string; avatar: string; role?: number } | null>(null);
  const [children, setChildren] = useState<StaffChildrenItem[]>([]);
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: -1, name: "我的" }]);
  const [loading, setLoading] = useState(true);

  const currentUid = useMemo(() => {
    const last = crumbs[crumbs.length - 1];
    return last?.id !== -1 ? last.id : undefined;
  }, [crumbs]);

  const loadRef = useRef(0);

  const loadChildren = async (uid?: number) => {
    const seq = ++loadRef.current;
    setLoading(true);
    const h = toastLoading("加载中...");
    try {
      const list = await getChildren(uid);
      if (seq !== loadRef.current) return;
      setChildren(Array.isArray(list) ? list : []);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        err?.message ||
        "";
      toastWarning(msg || "加载失败");
    } finally {
      h.close();
      if (seq === loadRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const info = await getStaffDetail();
        setMe({
          nickname: info.nickname || info.name || "--",
          avatar: info.avatar || info.thumb || "",
          role: info.role,
        });
      } catch {
        // request 层统一 toast
      }
    })();
  }, []);

  useEffect(() => {
    loadChildren(currentUid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUid]);

  const handleCrumbClick = (c: Crumb) => {
    const idx = crumbs.findIndex((x) => x.id === c.id);
    if (idx < 0 || idx === crumbs.length - 1) return;
    setCrumbs((prev) => prev.slice(0, idx + 1));
  };

  const handleClickItem = (item: StaffChildrenItem) => {
    const hasChildren = !!item.is_has_children;
    if (!hasChildren) return;
    setCrumbs((prev) => [...prev, { id: item.uid, name: item.nickname }]);
  };

  return (
    <div className="min-h-screen" style={{ background: "#F7F7F7", fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}>
      {/* Header */}
      <div className="bg-white px-5 flex items-center gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)", paddingBottom: 13, borderBottom: "1px solid #F5F5F5" }}>
        <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] transition-transform shrink-0" style={{ background: "#F3F4F6" }}>
          <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#374151" }} />
        </button>
        <span className="text-lg font-bold text-gray-800">人员关系</span>
      </div>

      <div className="p-4 space-y-3 pb-20">
        {/* 顶部人员卡片（参考 staffParent 区域，这里展示当前账号） */}
        <div className="bg-white rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl overflow-hidden bg-gray-100 shrink-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              {!!me?.avatar && <img src={me.avatar} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold text-gray-900 truncate">{me?.nickname || "--"}</div>
              <div className="mt-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{ border: `1px solid ${BRAND}`, color: BRAND }}>
                {roleName(me?.role)}
              </div>
            </div>
          </div>
        </div>

        {/* 面包屑 + 列表 */}
        <div className="bg-white rounded-2xl p-4">
          <div className="flex flex-wrap items-center gap-2">
            {crumbs.map((c, i) => (
              <button
                key={`${c.id}-${i}`}
                type="button"
                onClick={() => handleCrumbClick(c)}
                className="text-xs font-semibold px-2.5 py-1 rounded-full active:scale-[0.98] transition-all"
                style={{
                  background: i === crumbs.length - 1 ? `${BRAND}15` : "#F3F4F6",
                  color: i === crumbs.length - 1 ? BRAND : "#6B7280",
                }}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="pt-4 space-y-2">
            {children.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleClickItem(item)}
                className="w-full flex items-center gap-3 p-3 rounded-2xl active:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  {!!item.avatar && <img src={item.avatar} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-bold text-gray-900 truncate">{item.nickname}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    状态: {item.status === 1 ? "正常" : "禁用"}{" "}
                    创建时间: {item.create_time ? dayjs(item.create_time * 1000).format("YY-MM-DD") : "--"}
                  </div>
                </div>
                {!!item.is_has_children && (
                  <i className="ri-arrow-right-s-line text-gray-300 text-lg" />
                )}
              </button>
            ))}

            {!loading && children.length === 0 && (
              <div className="pt-10 pb-8 text-center">
                <i className="ri-user-search-line text-5xl block mx-auto mb-3 text-gray-200" />
                <p className="text-sm text-gray-400">暂无下级人员</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

